import React, { useState, useEffect, useMemo } from 'react';
import ScrollX from '../components/ScrollX';
import { useYearRange } from '../constants/yearRange';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import { loadExpectedBills, groupForTransmittals } from '../utils/expectedBills';
import { rolesForClient, staffNameFrom } from '../utils/assignments';
import useAssignments from '../hooks/useAssignments';
import { resolveTbtRecipients } from '../utils/tbtRecipients';
import { TAX_BILL_STATUS } from '../constants/statuses';

// Tax Bill Transmittals (Bubble 3, CS side). Queue = properties with ≥1 APPROVED bill (cleared the C2a file/transmit
// fork → transmittal-bound). Nested like Approvals: property card → (collector, installment) sub-card, where EACH sub
// with approved > 0 IS one TBT (1:1 with the email/PDF that D2/D3 will build). The logged-in CS's properties sort to
// top; all others visible below. Each sub row expands (one at a time) to a recipient picker + Generate → the
// TBT PDF (api/generate-tbt) with an in-panel preview; the "✓ Generated" marker shows at the row level. Send (D3) next.
// MONEY RULE: the dollar totals come from a supplementary tax_bills read SCOPED to the tax_bill_ids expected_bills
// returned (C2b guarantees those are live, superseded_at IS NULL) — a superseded bill's amount can never leak in.

const parseLocal = (s) => { if (!s) return null; const [y, m, d] = String(s).split('-').map(Number); return (y && m && d) ? new Date(y, m - 1, d) : null; };
const fmtDate = (s) => { const d = parseLocal(s); return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; };
const fmtMoney = (v) => (v == null || v === '') ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const currentTaxYear = () => sessionStorage.getItem('wh_valueYear') || String(new Date().getFullYear());
// Stable per-sub key — also the tax_bill_transmittals row shape: property|collector|installment.
const txKey = (propertyId, collectorId, inst) => `${propertyId}|${collectorId}|${inst ?? '-'}`;

export default function TaxBillTransmittals({ darkMode, staffUser }) {
  // Year selector range ← folder_year_config (Admin → Year Management), one source for every
  // value-year picker in the app. Falls back to 2021-2028 if the config row can't be read.
  const { years } = useYearRange();
  const isDark = darkMode;
  const c = isDark
    ? { card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc', input: 'var(--ink)', inputBorder: '#434C68', subCard: 'rgba(26,34,56,0.5)', rowBorder: 'rgba(67,76,104,0.25)', mine: 'rgba(180,0,0,0.35)' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068', input: '#ffffff', inputBorder: '#c8ccd8', subCard: '#f7f7fb', rowBorder: '#eef0f6', mine: 'var(--red)' };
  const fontStack = "'Public Sans',system-ui,sans-serif";
  const s = {
    input: { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '8px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: fontStack },
    th: { textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: c.muted },
    cellTd: { padding: '9px 12px', fontSize: '13px', color: c.text, borderBottom: `1px solid ${c.rowBorder}` },
    btnPrimary: { padding: '8px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: fontStack, whiteSpace: 'nowrap' },
    btnGhost: { padding: '7px 12px', background: 'transparent', border: `1px solid ${c.inputBorder}`, borderRadius: '8px', color: c.sub, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: fontStack, whiteSpace: 'nowrap' },
  };

  const [year, setYear] = useState(currentTaxYear);
  const [rows, setRows] = useState([]);
  const [billInfo, setBillInfo] = useState({});   // { tax_bill_id: { amount, due_date } } — supplementary, live-scoped (billed-authoritative)
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { assignments, staff } = useAssignments();

  // ── TBT generation (Stage 3.3) ──
  const [transmittals, setTransmittals] = useState({}); // subKey -> live tax_bill_transmittals row (this year)
  const [expandedSub, setExpandedSub] = useState(null); // subKey of the open panel (one at a time)
  const [recips, setRecips] = useState({ to: [], cc: [] });
  const [recipSource, setRecipSource] = useState('manual');
  const [newRecip, setNewRecip] = useState({ to: '', cc: '' });
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [regenMode, setRegenMode] = useState(false);
  // ── Transmit (Phase 4.2) ──
  const [transmitFor, setTransmitFor] = useState(null); // { g, sub, t } — the transmit modal target
  const [txStep, setTxStep] = useState('edit');         // 'edit' (recipients + download) | 'sent' (confirm-sent)
  const [txBusy, setTxBusy] = useState(false);
  const [txRecips, setTxRecips] = useState({ to: [], cc: [] });
  const [txNewRecip, setTxNewRecip] = useState({ to: '', cc: '' });
  const [toast, setToast] = useState(null);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 5000); return () => clearTimeout(t); }, [toast]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadExpectedBills(year).then(data => { if (alive) { setRows(data); setLoading(false); } });
    return () => { alive = false; };
  }, [year]);

  // Supplementary money read — ONLY the tax_bill_ids expected_bills returned for approved rows (live-guaranteed by
  // C2b). Never query tax_bills independently of this id set, or a superseded amount could skew a Total Amount Due.
  useEffect(() => {
    const ids = [...new Set(rows.filter(r => r.workflow_status === 'approved' && r.tax_bill_id).map(r => r.tax_bill_id))];
    if (!ids.length) { setBillInfo({}); return; }
    let alive = true;
    supabase.from('tax_bills').select('id,total_bill_amount,due_date,paid_source').in('id', ids)
      .then(({ data }) => { if (alive) setBillInfo(Object.fromEntries((data || []).map(b => [b.id, { amount: b.total_bill_amount, due_date: b.due_date, paid: !!b.paid_source }]))); });
    return () => { alive = false; };
  }, [rows]);

  // Live transmittals for the year → per-sub "Generated" status marker + preview. Keyed property|collector|installment.
  useEffect(() => {
    let alive = true;
    supabase.from('tax_bill_transmittals').select('*').eq('tax_year', String(year)).is('superseded_at', null)
      .then(({ data }) => { if (alive) setTransmittals(Object.fromEntries((data || []).map(t => [txKey(t.property_id, t.collector_id, t.installment_number), t]))); });
    return () => { alive = false; };
  }, [year]);

  // The property's client's CS (per-client, not state-scoped) → id + name + is-this-me.
  const csFor = useMemo(() => (clientId) => {
    const id = rolesForClient(assignments, clientId, 'CS')[0]?.staff_id || null;
    return { id, name: id ? staffNameFrom(staff, id) : '', mine: !!id && String(id) === String(staffUser?.id) };
  }, [assignments, staff, staffUser]);

  const groups = useMemo(() => groupForTransmittals(rows), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = groups;
    if (q) list = list.filter(g => [g.property_name, g.client_name, g.state, ...g.subs.map(su => su.collector_name)].some(v => (v || '').toLowerCase().includes(q)));
    // My properties (as CS) first, then property name.
    return [...list].sort((a, b) => {
      const am = csFor(a.client_id).mine ? 0 : 1, bm = csFor(b.client_id).mine ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.property_name || '').localeCompare(b.property_name || '');
    });
  }, [groups, search, csFor]);

  // Per-TBT (sub-card) figures — only the APPROVED parcels are transmittal-bound. ⛔ A PAID bill (catch-up year) is
  // never transmittal-bound: approval files it; one left at 'approved' from before that rule is excluded here, and
  // api/generate-tbt refuses it too.
  const approvedParcels = (sub) => sub.parcels.filter(p => p.workflow_status === 'approved' && !billInfo[p.tax_bill_id]?.paid);
  const subTotal = (sub) => approvedParcels(sub).reduce((n, p) => n + (Number(billInfo[p.tax_bill_id]?.amount) || 0), 0);
  // Due date = the billed on-time date (tax_bills.due_date), matching the generated TBT — NOT the collector installment date.
  const subDue = (sub) => { const p = approvedParcels(sub)[0] || sub.parcels[0] || {}; return billInfo[p.tax_bill_id]?.due_date || p.due_date; };

  // ── TBT generation handlers ──
  const subK = (g, sub) => txKey(g.property_id, sub.collector_id, sub.installment_number);

  async function fetchPreview(t) {
    setPreviewUrl(null);
    if (!t?.file_exchange_id) return;
    const { data: fx } = await supabase.from('file_exchange').select('storage_path').eq('id', t.file_exchange_id).single();
    if (fx?.storage_path) {
      const url = await fileUrl(fx.storage_path, 'preview').catch(() => null);
      setPreviewUrl(url);
    }
  }

  async function openSub(g, sub) {
    const key = subK(g, sub);
    if (expandedSub === key) { setExpandedSub(null); setPreviewUrl(null); setRegenMode(false); return; }
    setExpandedSub(key); setRegenMode(false); setNewRecip({ to: '', cc: '' });
    const existing = transmittals[key];
    if (existing) {
      setRecips({ to: existing.recipient_to || [], cc: existing.recipient_cc || [] });
      setRecipSource(existing.recipient_source || 'manual');
      fetchPreview(existing);
    } else {
      setPreviewUrl(null);
      const r = await resolveTbtRecipients(g.property_id, approvedParcels(sub));   // TBT-role contacts at this property, scoped to the bill's parcel kinds
      setRecips({ to: r.to || [], cc: r.cc || [] }); setRecipSource(r.source || 'manual');
    }
  }

  async function reloadTransmittals() {
    const { data } = await supabase.from('tax_bill_transmittals').select('*').eq('tax_year', String(year)).is('superseded_at', null);
    const map = Object.fromEntries((data || []).map(t => [txKey(t.property_id, t.collector_id, t.installment_number), t]));
    setTransmittals(map);
    return map;
  }

  async function doGenerate(g, sub) {
    if (generating) return;
    setGenerating(true);
    try {
      const res = await fetch('/api/generate-tbt', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: g.property_id, collectorId: sub.collector_id, installmentNumber: sub.installment_number,
          taxYear: String(year), recipients: { to: recips.to, cc: recips.cc }, recipientSource: recipSource,
          staffId: staffUser?.id || null, staffName: staffUser?.name || null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) { window.alert('Generate failed: ' + (json.error || `HTTP ${res.status}`)); return; }
      const map = await reloadTransmittals();
      setRegenMode(false);
      const key = subK(g, sub);
      if (map[key]) fetchPreview(map[key]);
    } catch (e) { console.error('[generate-tbt]', e); window.alert('Generate error: ' + (e.message || e)); }
    finally { setGenerating(false); }
  }

  const addRecip = (field) => {
    const email = (newRecip[field] || '').trim();
    if (!email.includes('@')) return;
    setRecips(p => ({ ...p, [field]: [...p[field], { name: email.split('@')[0], email }] }));
    setNewRecip(p => ({ ...p, [field]: '' }));
  };
  const removeRecip = (field, i) => setRecips(p => ({ ...p, [field]: p[field].filter((_, idx) => idx !== i) }));

  // ── Transmit handlers ──
  function openTransmit(g, sub, t) {
    setTransmitFor({ g, sub, t }); setTxStep('edit'); setTxBusy(false);
    setTxRecips({ to: t.recipient_to || [], cc: t.recipient_cc || [] }); setTxNewRecip({ to: '', cc: '' });
  }
  const txAddRecip = (field) => {
    const email = (txNewRecip[field] || '').trim();
    if (!email.includes('@')) return;
    setTxRecips(p => ({ ...p, [field]: [...p[field], { name: email.split('@')[0], email }] }));
    setTxNewRecip(p => ({ ...p, [field]: '' }));
  };
  const txRemoveRecip = (field, i) => setTxRecips(p => ({ ...p, [field]: p[field].filter((_, idx) => idx !== i) }));

  async function downloadEml() {
    if (txBusy || !transmitFor) return;
    // Flush any typed-but-not-Entered email in the input into the chips — a forgotten Enter must NOT silently
    // drop the recipient (the .eml would then open with an empty To/Cc, which is what bit the CA transmit).
    const flush = (list, pending) => { const e = (pending || '').trim(); return (e.includes('@') && !list.some(r => r && r.email === e)) ? [...list, { name: e.split('@')[0], email: e }] : list; };
    const toL = flush(txRecips.to, txNewRecip.to);
    const ccL = flush(txRecips.cc, txNewRecip.cc);
    if (!toL.filter(r => r && r.email).length) { window.alert('Add at least one To recipient.'); return; }
    setTxRecips({ to: toL, cc: ccL }); setTxNewRecip({ to: '', cc: '' });   // reflect the flush in the picker
    setTxBusy(true);
    try {
      const { t } = transmitFor;
      // Persist any recipient edits back to the transmittal so the record matches what was sent.
      await supabase.from('tax_bill_transmittals').update({ recipient_to: toL, recipient_cc: ccL }).eq('id', t.id);
      const res = await fetch('/api/build-tbt-eml', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transmittalId: t.id, to: toL, cc: ccL }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) { window.alert('Build .eml failed: ' + (json.error || `HTTP ${res.status}`)); return; }
      const blob = new Blob([json.eml], { type: 'message/rfc822' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = json.filename || 'transmittal.eml';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      setTxStep('sent');
    } catch (e) { console.error('[build-eml]', e); window.alert('Build .eml error: ' + (e.message || e)); }
    finally { setTxBusy(false); }
  }

  async function markTransmitted() {
    if (txBusy || !transmitFor) return;
    setTxBusy(true);
    try {
      const { t } = transmitFor;
      const { error: e1 } = await supabase.from('tax_bill_transmittals').update({ transmitted_at: new Date().toISOString(), transmitted_by: staffUser?.id || null }).eq('id', t.id);
      if (e1) { window.alert('Mark transmitted failed: ' + e1.message); return; }
      // Flip the member bills to 'transmitted' → they leave the approved-based queue (Option B: work queue).
      const { error: e2 } = await supabase.from('tax_bills').update({ status: TAX_BILL_STATUS.TRANSMITTED }).eq('transmittal_id', t.id);
      if (e2) { window.alert('Flip bills failed: ' + e2.message); return; }
      setTransmitFor(null); setExpandedSub(null); setPreviewUrl(null);
      setToast('Transmitted — this sub leaves the queue. The TBT stays filed under the property’s Documents (Tax Bills).');
      loadExpectedBills(year).then(data => setRows(data));   // the transmitted sub drops out (bills no longer approved)
      reloadTransmittals();
    } catch (e) { console.error('[mark-transmitted]', e); window.alert('Error: ' + (e.message || e)); }
    finally { setTxBusy(false); }
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text }}>Tax Bill Transmittals</div>
        <select value={year} onChange={e => setYear(e.target.value)} style={{ ...s.input, width: 'auto' }}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>Approved bills ready to transmit for {year} — one transmittal per collector/installment. Your clients are pinned to the top.</div>

      <div style={{ maxWidth: '360px', marginBottom: '16px' }}>
        <input style={s.input} placeholder="Search client, property, collector…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading
        ? <div style={{ color: c.muted, fontSize: '13px' }}>Loading…</div>
        : filtered.length === 0
          ? <div style={{ color: c.muted, fontSize: '13px', padding: '24px 0', textAlign: 'center' }}>{search ? 'No matching transmittals.' : 'Nothing ready to transmit.'}</div>
          : filtered.map(g => {
            const cs = csFor(g.client_id);
            const tbtSubs = g.subs.filter(sub => sub.approved > 0);   // each = one TBT
            return (
              <div key={g.property_id} style={{ background: c.card, border: `1px solid ${cs.mine ? c.mine : c.border}`, borderRadius: '12px', padding: '16px 18px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: c.text }}>{g.property_name || '—'}</div>
                    <div style={{ fontSize: '12px', color: c.muted }}>{g.client_name || '—'}{g.state ? ` · ${g.state}` : ''}</div>
                  </div>
                  <div style={{ fontSize: '12px', color: c.sub }}>CS: <span style={{ fontWeight: '700', color: cs.mine ? (isDark ? '#fca5a5' : 'var(--red)') : c.text }}>{cs.name || '—'}{cs.mine ? ' (you)' : ''}</span></div>
                </div>

                <ScrollX style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['Collector', 'Installment', 'Due Date', 'Parcels', 'Total Amount Due', 'TBT'].map((h, i) => (
                        <th key={h} style={{ ...s.th, padding: '6px 12px', textAlign: (i === 3 || i === 4) ? 'right' : 'left', borderBottom: `1px solid ${c.rowBorder}` }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {tbtSubs.map(sub => {
                        const key = subK(g, sub);
                        const t = transmittals[key];
                        const open = expandedSub === key;
                        const showPicker = !t || regenMode;
                        return (
                          <React.Fragment key={key}>
                            {/* Collapsed scanning row — the "Generated" marker is visible without expanding */}
                            <tr onClick={() => openSub(g, sub)} style={{ background: open ? c.card : c.subCard, cursor: 'pointer' }}>
                              <td style={s.cellTd}>{sub.collector_name || '—'}</td>
                              <td style={s.cellTd}>{sub.installment_number ?? '—'}</td>
                              <td style={s.cellTd}>{fmtDate(subDue(sub))}</td>
                              <td style={{ ...s.cellTd, textAlign: 'right' }}>{sub.approved}</td>
                              <td style={{ ...s.cellTd, textAlign: 'right', fontWeight: '700' }}>{fmtMoney(subTotal(sub))}</td>
                              <td style={s.cellTd}>
                                {t
                                  ? <span style={{ color: '#059669', fontWeight: '700', fontSize: '12px' }}>✓ Generated <span style={{ color: c.muted, fontWeight: '400' }}>· {fmtDate(String(t.generated_at || '').slice(0, 10))}</span></span>
                                  : <span style={{ color: c.muted, fontSize: '12px' }}>Not generated</span>}
                                <span style={{ color: c.muted, marginLeft: '8px' }}>{open ? '▾' : '▸'}</span>
                              </td>
                            </tr>
                            {/* Expanded panel — recipient picker + Generate, or the generated PDF preview */}
                            {open && (
                              <tr>
                                <td colSpan={6} style={{ padding: '0 12px 16px', background: c.card, borderBottom: `1px solid ${c.rowBorder}` }}>
                                  {showPicker ? (
                                    <div style={{ paddingTop: '12px' }}>
                                      <div style={{ fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                                        Recipients{recipSource === 'portal_users' ? <span style={{ fontWeight: '400', textTransform: 'none' }}> · seeded from portal contacts</span> : null}
                                      </div>
                                      {['to', 'cc'].map(field => (
                                        <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                          <span style={{ fontSize: '12px', fontWeight: '700', color: c.sub, width: '26px', textTransform: 'uppercase' }}>{field}</span>
                                          {recips[field].map((r, i) => (
                                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: c.subCard, border: `1px solid ${c.border}`, borderRadius: '14px', padding: '3px 10px', fontSize: '12px', color: c.text }}>
                                              {r.email}
                                              <span onClick={() => removeRecip(field, i)} style={{ cursor: 'pointer', color: c.muted, fontWeight: '700' }}>×</span>
                                            </span>
                                          ))}
                                          <input value={newRecip[field]} onChange={e => setNewRecip(p => ({ ...p, [field]: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRecip(field); } }}
                                            placeholder="add email + Enter" style={{ ...s.input, width: '190px', padding: '5px 9px', fontSize: '12px' }} />
                                        </div>
                                      ))}
                                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'center' }}>
                                        <button disabled={generating} onClick={() => doGenerate(g, sub)} style={generating ? { ...s.btnPrimary, opacity: 0.6, cursor: 'default' } : s.btnPrimary}>
                                          {generating ? 'Generating…' : (t ? '↻ Regenerate TBT' : '⚙ Generate TBT')}
                                        </button>
                                        {regenMode && <button onClick={() => { setRegenMode(false); if (t) fetchPreview(t); }} style={s.btnGhost}>Cancel</button>}
                                        <span style={{ fontSize: '11px', color: c.muted }}>The county's original bill is attached alongside this TBT when transmitted.</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ paddingTop: '12px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                        <div style={{ fontSize: '12px', color: c.muted }}>
                                          {(t.recipient_to || []).length > 0
                                            ? <>To: <b style={{ color: c.text }}>{(t.recipient_to || []).map(r => r.email).join(', ')}</b></>
                                            : <span style={{ color: 'var(--red)' }}>No recipients set — Regenerate to add.</span>}
                                          {(t.recipient_cc || []).length > 0 && <> · Cc: {(t.recipient_cc || []).map(r => r.email).join(', ')}</>}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                          {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer" style={{ ...s.btnGhost, textDecoration: 'none' }}>⤓ Download</a>}
                                          <button onClick={() => setRegenMode(true)} style={s.btnGhost}>↻ Regenerate</button>
                                          <button onClick={() => openTransmit(g, sub, t)} style={s.btnPrimary}>📤 Transmit</button>
                                        </div>
                                      </div>
                                      {previewUrl
                                        ? <iframe src={`${previewUrl}#toolbar=1&navpanes=0&view=FitH`} title="TBT preview" style={{ width: '100%', height: '520px', border: `1px solid ${c.border}`, borderRadius: '8px', background: '#fff' }} />
                                        : <div style={{ fontSize: '12px', color: c.muted, padding: '16px 0' }}>Loading preview…</div>}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </ScrollX>
              </div>
            );
          })}

      {/* Transmit modal — confirm/edit recipients → download .eml → confirm-sent → mark transmitted */}
      {transmitFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '14px', padding: '22px 24px', maxWidth: '540px', width: '100%' }}>
            <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: '700', color: c.text }}>Transmit — {transmitFor.g.property_name || '—'}</div>
            <div style={{ fontSize: '12px', color: c.muted, marginBottom: '16px' }}>{transmitFor.sub.collector_name || '—'} · Installment {transmitFor.sub.installment_number ?? '—'}</div>
            {txStep === 'edit' ? (
              <>
                <div style={{ fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Confirm recipients</div>
                {['to', 'cc'].map(field => (
                  <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: c.sub, width: '26px', textTransform: 'uppercase' }}>{field}</span>
                    {txRecips[field].map((r, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: c.subCard, border: `1px solid ${c.border}`, borderRadius: '14px', padding: '3px 10px', fontSize: '12px', color: c.text }}>
                        {r.email}<span onClick={() => txRemoveRecip(field, i)} style={{ cursor: 'pointer', color: c.muted, fontWeight: '700' }}>×</span>
                      </span>
                    ))}
                    <input value={txNewRecip[field]} onChange={e => setTxNewRecip(p => ({ ...p, [field]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); txAddRecip(field); } }}
                      placeholder="add email + Enter" style={{ ...s.input, width: '190px', padding: '5px 9px', fontSize: '12px' }} />
                  </div>
                ))}
                <div style={{ fontSize: '11px', color: c.muted, margin: '12px 0 18px', lineHeight: 1.5 }}>Downloads a <b>.eml</b> — double-click it to open Outlook with the <b>TBT + county bill attached</b> and To/Cc filled, ready to send.</div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button style={s.btnGhost} onClick={() => setTransmitFor(null)}>Cancel</button>
                  <button style={txBusy ? { ...s.btnPrimary, opacity: 0.6, cursor: 'default' } : s.btnPrimary} disabled={txBusy} onClick={downloadEml}>{txBusy ? 'Building…' : '📥 Download .eml & open Outlook'}</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '13px', color: c.text, lineHeight: 1.55, marginBottom: '18px' }}>
                  The <b>.eml</b> downloaded. Double-click it → Outlook opens a message with the <b>TBT + county bill attached</b> and To/Cc filled. <b>Send it in Outlook</b>, then confirm below to mark this transmitted — it will leave the queue.
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button style={s.btnGhost} onClick={() => setTransmitFor(null)}>Not yet</button>
                  <button style={txBusy ? { ...s.btnPrimary, opacity: 0.6, cursor: 'default' } : s.btnPrimary} disabled={txBusy} onClick={markTransmitted}>{txBusy ? 'Saving…' : '✓ Mark as transmitted'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Transient confirmation banner */}
      {toast && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: '#0e7490', color: '#fff', padding: '11px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', boxShadow: '0 6px 20px rgba(0,0,0,0.25)', zIndex: 1100, fontFamily: fontStack, maxWidth: '540px', textAlign: 'center' }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
}
